import {
  IfIsaTransfer,
  IfIsaTransferStatus,
  IfIsaTransferType,
} from "@ifgengineering/client-invest-sdk";
import { theme } from "@ifgengineering/component-library";

export const statusTextAndColorsMap: Partial<
  Record<
    IfIsaTransferStatus,
    {
      colorDark: keyof typeof theme.colors;
      colorLight: keyof typeof theme.colors;
      statusText: string;
    }
  >
> = {
  IN_PROGRESS: {
    colorDark: "GOLD500",
    colorLight: "GOLD100",
    statusText: "IN PROGRESS",
  },
  COMPLETE: {
    colorDark: "GREEN600",
    colorLight: "GREEN100",
    statusText: "COMPLETED",
  },
  REJECTED: {
    colorDark: "ERROR800",
    colorLight: "ERROR100",
    statusText: "REJECTED",
  },
  CANCELLED: {
    colorDark: "SLATE500",
    colorLight: "SLATE100",
    statusText: "CANCELLED",
  },
};

export const mockIfIsaTransfers: IfIsaTransfer[] = [
  {
    id: "trf_req_001",
    ifIsaAccountId: 101,
    dealId: 0,
    currentManagerName: "Alpha ISA Managers",
    currentManagerAddress: "1 Market St, London",
    totalAmount: 15000,
    instructionDate: new Date("2025-08-01T10:00:00.000Z").toISOString(),
    completionDate: null,
    transactions: [
      {
        amount: 9000,
        taxYear: "2024/25",
        currentTaxYearAmount: null,
        transferType: "PARTIAL",
        description: "Partial transfer of previous years’ subscriptions",
        isaType: "STOCKS_SHARES",
        id: "txn_001",
        ifIsaAccountId: 101,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-01T10:00:00.000Z").toISOString(),
      },
      {
        amount: 6000,
        taxYear: "2025/26",
        currentTaxYearAmount: 6000,
        transferType: "CURRENT_YEAR",
        description: "Current year subscriptions to date",
        isaType: "CASH",
        id: "txn_002",
        ifIsaAccountId: 101,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-01T10:00:00.000Z").toISOString(),
      },
    ],
    transferStatus: "REQUESTED",
    noticeDecision: "SERVE_NOTICED",
    notes: "Initial request submitted by investor.",
    createdAt: new Date("2025-08-01T10:00:00.000Z").toISOString(),
    updatedAt: new Date("2025-08-01T10:05:00.000Z").toISOString(),
  },
  {
    id: "trf_prep_002",
    ifIsaAccountId: 102,
    dealId: 2001,
    currentManagerName: "Beta Wealth",
    currentManagerAddress: "22 Fleet St, London",
    totalAmount: 27500,
    instructionDate: new Date("2025-08-05T09:30:00.000Z").toISOString(),
    completionDate: null,
    transactions: [
      {
        amount: 20000,
        taxYear: "2023/24",
        currentTaxYearAmount: null,
        transferType: "FULL",
        description: "Full transfer of previous ISA",
        isaType: "IF_ISA",
        id: "txn_001",
        ifIsaAccountId: 102,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-05T09:30:00.000Z").toISOString(),
      },
      {
        amount: 7500,
        taxYear: "2025/26",
        currentTaxYearAmount: 7500,
        transferType: "CURRENT_YEAR",
        description: "Current year contributions",
        isaType: "CASH",
        id: "txn_002",
        ifIsaAccountId: 102,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-05T09:30:00.000Z").toISOString(),
      },
    ],
    transferStatus: "PREPARING",
    noticeDecision: "SERVE_NOTICED",
    notes: "Documents verified; awaiting provider pack.",
    createdAt: new Date("2025-08-05T09:30:00.000Z").toISOString(),
    updatedAt: new Date("2025-08-06T14:45:00.000Z").toISOString(),
  },

  // Raw status: SENT_TO_PROVIDER → derived "IN_PROGRESS"
  {
    id: "trf_sent_003",
    ifIsaAccountId: 103,
    dealId: 2002,
    currentManagerName: "Gamma Investments",
    currentManagerAddress: "5 King’s Rd, Manchester",
    totalAmount: 32000,
    instructionDate: new Date("2025-08-10T08:00:00.000Z").toISOString(),
    completionDate: null,
    transactions: [
      {
        amount: 25000,
        taxYear: "2024/25",
        currentTaxYearAmount: null,
        transferType: "PARTIAL",
        description: "Partial transfer of previous years",
        isaType: "STOCKS_SHARES",
        id: "txn_001",
        ifIsaAccountId: 103,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-10T08:00:00.000Z").toISOString(),
      },
      {
        amount: 7000,
        taxYear: "2025/26",
        currentTaxYearAmount: 7000,
        transferType: "CURRENT_YEAR",
        description: "Current year to date",
        isaType: "CASH",
        id: "txn_002",
        ifIsaAccountId: 103,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-10T08:00:00.000Z").toISOString(),
      },
    ],
    transferStatus: "SENT_TO_PROVIDER",
    noticeDecision: "SERVE_NOTICED",
    notes: "Transfer forms sent to existing provider.",
    createdAt: new Date("2025-08-10T08:00:00.000Z").toISOString(),
    updatedAt: new Date("2025-08-12T11:10:00.000Z").toISOString(),
  },
  {
    id: "trf_prog_004",
    ifIsaAccountId: 104,
    dealId: 0,
    currentManagerName: "Delta Capital",
    currentManagerAddress: null,
    totalAmount: 10000,
    instructionDate: new Date("2025-08-12T12:00:00.000Z").toISOString(),
    completionDate: null,
    transactions: [
      {
        amount: 10000,
        taxYear: "2025/26",
        currentTaxYearAmount: 10000,
        transferType: "CURRENT_YEAR",
        description: "Current year subscriptions",
        isaType: "IF_ISA",
        transactionType: "DEPOSIT",
        id: "txn_001",
        ifIsaAccountId: 104,
        createdAt: new Date("2025-08-12T12:00:00.000Z").toISOString(),
      },
    ],
    transferStatus: "IN_PROGRESS",
    noticeDecision: "SERVE_NOTICED",
    notes: "Awaiting funds release from current manager.",
    createdAt: new Date("2025-08-12T12:00:00.000Z").toISOString(),
    updatedAt: new Date("2025-08-15T12:00:00.000Z").toISOString(),
  },
  {
    id: "trf_done_005",
    ifIsaAccountId: 105,
    dealId: 2003,
    currentManagerName: "Epsilon Trust",
    currentManagerAddress: "18 Castle St, Edinburgh",
    totalAmount: 50000,
    instructionDate: new Date("2025-07-20T09:15:00.000Z").toISOString(),
    completionDate: new Date("2025-08-20T16:30:00.000Z").toISOString(),
    transactions: [
      {
        amount: 40000,
        taxYear: "2023/24",
        currentTaxYearAmount: null,
        transferType: "FULL",
        description: "Full transfer from previous provider",
        isaType: "IF_ISA",
        id: "txn_001",
        ifIsaAccountId: 105,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-07-20T09:15:00.000Z").toISOString(),
      },
      {
        amount: 10000,
        taxYear: "2025/26",
        currentTaxYearAmount: 10000,
        transferType: "CURRENT_YEAR",
        description: "Current year top-up",
        isaType: "CASH",
        id: "txn_002",
        ifIsaAccountId: 105,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-07-20T09:15:00.000Z").toISOString(),
      },
    ],
    transferStatus: "COMPLETE",
    noticeDecision: "SERVE_NOTICED",
    notes: "All funds received and allocated.",
    createdAt: new Date("2025-07-20T09:15:00.000Z").toISOString(),
    updatedAt: new Date("2025-08-20T16:30:00.000Z").toISOString(),
  },
  {
    id: "trf_rej_006",
    ifIsaAccountId: 106,
    dealId: 0,
    currentManagerName: "Zeta Financial",
    currentManagerAddress: "90 River Rd, Bristol",
    totalAmount: 8000,
    instructionDate: new Date("2025-08-02T11:45:00.000Z").toISOString(),
    completionDate: new Date("2025-08-07T10:20:00.000Z").toISOString(),
    transactions: [
      {
        amount: 8000,
        taxYear: "2024/25",
        currentTaxYearAmount: null,
        transferType: "PARTIAL",
        description: "Partial transfer attempt rejected by provider",
        isaType: "CASH",
        id: "txn_001",
        ifIsaAccountId: 106,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-02T11:45:00.000Z").toISOString(),
      },
    ],
    transferStatus: "REJECTED",
    noticeDecision: "SERVE_NOTICED",
    notes: "Provider rejected due to signature mismatch.",
    createdAt: new Date("2025-08-02T11:45:00.000Z").toISOString(),
    updatedAt: new Date("2025-08-07T10:20:00.000Z").toISOString(),
  },
  {
    id: "trf_can_007",
    ifIsaAccountId: 107,
    dealId: 2004,
    currentManagerName: "Omega Wealth",
    currentManagerAddress: "7 Harbour Ave, Liverpool",
    totalAmount: 12000,
    instructionDate: new Date("2025-08-03T13:00:00.000Z").toISOString(),
    completionDate: new Date("2025-08-04T09:00:00.000Z").toISOString(),
    transactions: [
      {
        amount: 7000,
        taxYear: "2024/25",
        currentTaxYearAmount: null,
        transferType: "PARTIAL",
        description: "Previous year partial transfer",
        isaType: "STOCKS_SHARES",
        id: "txn_001",
        ifIsaAccountId: 107,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-03T13:00:00.000Z").toISOString(),
      },
      {
        amount: 5000,
        taxYear: "2025/26",
        currentTaxYearAmount: 5000,
        transferType: "CURRENT_YEAR",
        description: "Current year allocation (cancelled)",
        isaType: "CASH",
        id: "txn_002",
        ifIsaAccountId: 107,
        transactionType: "DEPOSIT",
        createdAt: new Date("2025-08-03T13:00:00.000Z").toISOString(),
      },
    ],
    transferStatus: "CANCELLED",
    noticeDecision: "SERVE_NOTICED",
    notes: "Cancelled by investor prior to provider processing.",
    createdAt: new Date("2025-08-03T13:00:00.000Z").toISOString(),
    updatedAt: new Date("2025-08-04T09:00:00.000Z").toISOString(),
  },
];

export const OTHER_PROVIDER_OPTION = {
  label: "Other",
  value: "other",
};

export interface IsaManagerDetails {
  name: string;
  address: string;
  address2?: string;
  address3?: string;
  postcode: string;
}

export const ISA_MANAGERS: IsaManagerDetails[] = [
  {
    name: "A J Bell",
    address: "4 Exchange Quay",
    address2: "Salford Quays",
    address3: "Manchester",
    postcode: "M5 3EE",
  },
  {
    name: "Al Rayan Bank",
    address: "PO Box 12461",
    address2: "Birmingham",
    address3: "",
    postcode: "B16 6AQ",
  },
  {
    name: "Barclays Bank",
    address: "1 Churchill Place",
    address2: "London",
    address3: "",
    postcode: "E14 5HP",
  },
  {
    name: "Cater Allen Private Bank",
    address: "2 Triton Square",
    address2: "Regent's Place",
    address3: "London",
    postcode: "NW1 3AN",
  },
  {
    name: "Chip Cash ISA",
    address: "Sixth Floor",
    address2: "Fora Montacute Yards",
    address3: "186 Shoreditch High Street, London",
    postcode: "E1 6HU",
  },
  {
    name: "Fidelity",
    address: "PO Box 391",
    address2: "Tadworth",
    address3: "Surrey",
    postcode: "KT20 9FU",
  },
  {
    name: "First Direct",
    address: "40 Wakefield Road",
    address2: "Leeds",
    address3: "",
    postcode: "LS98 1FD",
  },
  {
    name: "Foresters Financial",
    address: "2 Cromwell Avenue",
    address2: "Bromley",
    address3: "",
    postcode: "BR2 9BF",
  },
  {
    name: "Freetrade (web.freetrade.io)",
    address: "145 City Road",
    address2: "London",
    address3: "England",
    postcode: "EC1V 1AZ",
  },
  {
    name: "Gatehouse Bank",
    address: "PO Box 861",
    address2: "Wallsend",
    address3: "",
    postcode: "NE28 5BP",
  },
  {
    name: "Halifax Building Society",
    address: "Trinity Road",
    address2: "Halifax",
    address3: "",
    postcode: "HX1 2RG",
  },
  {
    name: "Hargreaves Lansdown",
    address: "1 College Square South",
    address2: "Anchor Road",
    address3: "Bristol",
    postcode: "BS1 5HL",
  },
  {
    name: "IG",
    address: "Cannon Bridge House",
    address2: "25 Dowgate Hill",
    address3: "London",
    postcode: "EC4R 2YA",
  },
  {
    name: "IWeb Share Dealing",
    address: "Lovell Park Road",
    address2: "Leeds",
    address3: "",
    postcode: "LS1 1NS",
  },
  {
    name: "Interactive Investor",
    address: "2nd floor",
    address2: "One Embankment",
    address3: "Neville Street, Leeds",
    postcode: "LS1 4DW",
  },
  {
    name: "InvestEngine",
    address: "57-59 Great Suffolk Street",
    address2: "London",
    address3: "",
    postcode: "SE1 0BB",
  },
  {
    name: "Loanpad",
    address: "5 Technology Park",
    address2: "Colindeep Lane",
    address3: "Colindale, London",
    postcode: "NW9 6BX",
  },
  {
    name: "Moneyfarm",
    address: "90-92 Pentonville Road",
    address2: "London",
    address3: "",
    postcode: "N1 9HS",
  },
  {
    name: "NS&I (National Savings & Investments)",
    address: "Sunderland",
    address2: "",
    address3: "",
    postcode: "SR43 2SB",
  },
  {
    name: "Nester",
    address: "727, Salisbury House",
    address2: "Finsbury Circus",
    address3: "London",
    postcode: "EC2M 5SQ",
  },
  {
    name: "Nutmeg",
    address: "25 Bank Street",
    address2: "Canary Wharf",
    address3: "",
    postcode: "E14 5JP",
  },
  {
    name: "Santander",
    address: "Santander Uk",
    address2: "Bridle Road",
    address3: "Bootle",
    postcode: "L30 4GB",
  },
  {
    name: "Scottish Friendly",
    address: "Galbraith House",
    address2: "16 Blythswood Square",
    address3: "Glasgow",
    postcode: "G2 4HJ",
  },
  {
    name: "Shawbrook Bank",
    address: "Warley Hill Business Park",
    address2: "The Drive",
    address3: "Great Warley, Brentwood, Essex",
    postcode: "CM13 3BE",
  },
  {
    name: "Trading 212",
    address: "Aldermary House",
    address2: "10-15 Queen St",
    address3: "London",
    postcode: "EC4N 1TX",
  },
  {
    name: "Vanguard",
    address: "PO Box 24095",
    address2: "1 Tanfield",
    address3: "Edinburgh",
    postcode: "EH3 1FS",
  },
  {
    name: "Wahed",
    address: "87-89 Baker Street",
    address2: "London",
    address3: "UK",
    postcode: "W1U 6RJ",
  },
  {
    name: "Wealthify Limited",
    address: "Tec Marina",
    address2: "Terra Nova Way",
    address3: "Penarth",
    postcode: "CF64 1SA",
  },
];

export const IsaTransferAmountTypeMap: Record<
  IfIsaTransferType,
  "full" | "tax-year" | "partial"
> = {
  FULL: "full",
  PARTIAL: "partial",
  CURRENT_YEAR: "tax-year",
};
